import { useEffect, useMemo } from 'react';
import { useSekce } from '../../contexts/SekceContext';
import { zalozkyModulu } from '../../lib/moduly';

/**
 * TabBar — záložky modulu (norma FofrLama OS).
 *
 * **Záložky bere z katalogu**, ne ze seznamu v každé stránce. Dřív byly
 * definované dvakrát — jednou v `moduly.json` jako klíče přístupů, podruhé
 * v každé stránce jako pole `TABS` — a nakreslené osmkrát, pokaždé trochu
 * jinak: mezera 0/1/4 px, ikona 14/15/16 px, jednou inline styly, jindy
 * Tailwind. Dvojí seznam se navíc mohl rozejít: přejmenovaná podstránka
 * v katalogu zůstala ve stránce pod starým názvem a nikdo si toho nevšiml.
 *
 * Komponenta dělá tři věci, které se dosud opisovaly:
 *  1. vytáhne záložky z katalogu (název, pořadí, ikona)
 *  2. **odfiltruje je podle přístupů** (`modul.podstranka`) — tenhle řádek
 *     byl v osmi stránkách slovo od slova stejný
 *  3. na mobilu je **ohlásí liště** (`useSekce`), takže je přepíná střed;
 *     pruh se tam nekreslí, ukrojil by řádek obsahu
 *
 * Když uživateli zbyde jediná záložka, pruh se nekreslí — přepínač mezi
 * jednou možností není přepínač.
 *
 * ```jsx
 * const [tab, setTab] = useState(null);
 * <TabBar modul="flotila" user={user} aktivni={tab} onZmena={setTab}
 *         barva={SECTION_COLOR} isDark={isDark} isMobile={isMobile} />
 * ```
 *
 * Stránka si výchozí záložku neurčuje sama — `onZmena` dostane první
 * dostupnou hned po připojení, takže `aktivni` smí začít jako `null`.
 *
 * @param {string} props.modul - id modulu v katalogu (`flotila`, `finance`…)
 * @param {Object} props.user
 * @param {string|null} props.aktivni
 * @param {(id: string) => void} props.onZmena
 * @param {string} props.barva - akcentová barva sekce
 * @param {boolean} [props.isDark]
 * @param {boolean} [props.isMobile] - na mobilu pruh nekreslí, hlásí se liště
 * @param {string[]} [props.jen] - omezí nabídku na dané id (např. záložka
 *        vyhrazená adminovi); pořadí pořád určuje katalog
 * @param {React.ReactNode} [props.vpravo] - hlavní akce záložky u pravé hrany pruhu.
 *        Na mobilu, kde pruh není, stojí sama vpravo nad obsahem — akce sekce
 *        je tak ve všech modulech na stejném místě, ne pokaždé jinde.
 * @param {Object<string, number>} [props.pocty] - číslo u záložky (co čeká: úkoly, žádosti…)
 * @param {Array<{id: string, nazev: string, Icon?: Function, pred?: boolean}>} [props.navic]
 *        Záložky, které v katalogu nejsou a nejsou ani klíčem přístupu: osobní
 *        pohled („Moje vzdělávání"), nebo stránka mimo katalog (Konfigurace,
 *        Správce souborů — tam je `modul` prázdný a nabídku nesou jen tyhle).
 *        `pred: true` je postaví před katalogové, jinak jdou za ně.
 */
export default function TabBar({
    modul,
    user,
    aktivni,
    onZmena,
    barva,
    isDark = false,
    isMobile = false,
    jen = null,
    vpravo = null,
    navic = null,
    pocty = null,
}) {
    const zalozky = useMemo(() => {
        const zKatalogu = modul
            ? zalozkyModulu(modul, user).filter((z) => (jen ? jen.includes(z.id) : true))
            : [];
        if (!navic?.length) return zKatalogu;
        return [
            ...navic.filter((z) => z.pred),
            ...zKatalogu,
            ...navic.filter((z) => !z.pred),
        ];
        // `jen` i `navic` bývají pole skládaná při renderu — hlídá se obsah,
        // ne reference.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modul, user, jen ? jen.join('|') : '', navic ? navic.map((z) => `${z.id}:${z.nazev}`).join('|') : '']);

    // Výchozí záložka: první, ke které má uživatel přístup. Stránka tím pádem
    // nemusí vědět, co je „první" — pořadí patří katalogu.
    useEffect(() => {
        if (!zalozky.length) return;
        if (!aktivni || !zalozky.some((z) => z.id === aktivni)) onZmena?.(zalozky[0].id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zalozky, aktivni]);

    // Na mobilu záložky přepíná střed lišty — pruh nahoře by ukrojil řádek
    // obsahu a říkal totéž podruhé.
    useSekce(isMobile && zalozky.length > 1
        ? { sekce: zalozky.map((z) => ({ id: z.id, label: z.nazev, Icon: z.Icon })), aktivni, onVyber: onZmena }
        : null);

    if (isMobile || zalozky.length < 2) {
        return vpravo ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: isMobile ? 12 : 16 }}>
                {vpravo}
            </div>
        ) : null;
    }

    return (
        <div
            role="tablist"
            className="flex items-center gap-1 overflow-x-auto"
            style={{
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
            }}
        >
            {zalozky.map((z) => {
                const jeAktivni = aktivni === z.id;
                const Icon = z.Icon;
                return (
                    <button
                        key={z.id}
                        type="button"
                        role="tab"
                        aria-selected={jeAktivni}
                        onClick={() => onZmena?.(z.id)}
                        className="flex items-center gap-2 whitespace-nowrap transition-colors"
                        style={{
                            padding: '10px 16px',
                            fontSize: 14,
                            fontWeight: jeAktivni ? 600 : 500,
                            color: jeAktivni ? barva : 'var(--text-secondary)',
                            background: 'none',
                            border: 'none',
                            borderBottom: `2px solid ${jeAktivni ? barva : 'transparent'}`,
                            marginBottom: -1,
                            cursor: 'pointer',
                            flexShrink: 0,
                            font: 'inherit',
                            // Připravovaná záložka se dá otevřít (ukáže „Připravujeme"),
                            // ale nesmí vypadat jako hotová práce.
                            opacity: z.pripravovany && !jeAktivni ? 0.5 : 1,
                        }}
                    >
                        {Icon && <Icon size={15} strokeWidth={2} />}
                        {z.nazev}
                        {pocty?.[z.id] > 0 && (
                            <span className="tabular-nums" style={{ fontSize: 11, fontWeight: 700, minWidth: 18, padding: '1px 6px', borderRadius: 99, background: barva, color: '#fff', textAlign: 'center' }}>
                                {pocty[z.id] > 99 ? '99+' : pocty[z.id]}
                            </span>
                        )}
                        {z.pripravovany && (
                            <span style={{
                                fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                                padding: '1px 6px', borderRadius: 99,
                                border: '1px solid currentColor', opacity: 0.8,
                            }}>
                                PŘIPRAVUJEME
                            </span>
                        )}
                    </button>
                );
            })}
            {vpravo && <div style={{ marginLeft: 'auto', paddingLeft: 12 }}>{vpravo}</div>}
        </div>
    );
}
